import React from 'react';
import {
  FileText,
  Calendar,
  Settings,
  Folder as FolderIcon,
  ChevronRight,
  ChevronDown,
  Plus,
} from 'lucide-react';
import { File, Folder } from '../types';
import { Button } from './ui/button';
import EditFileDialog from './EditFileDialog';
import EditFolderDialog from './EditFolderDialog';

interface FileListProps {
  files: File[];
  folders?: Folder[];
  currentFile: File | null;
  onFileSelect: (file: File) => void;
  onFileDelete: (fileId: string) => void;
  onFileRename?: (fileId: string, newName: string) => Promise<void> | void;
  onCreateFolder?: (parentId?: string | null) => void;
  onRenameFolder?: (folderId: string, newName: string) => Promise<void> | void;
  onDeleteFolder?: (folderId: string) => Promise<void> | void;
  onMoveFile?: (
    fileId: string,
    folderId: string | null
  ) => Promise<void> | void;
  onMoveFolder?: (
    folderId: string,
    parentId: string | null
  ) => Promise<void> | void;
  loading: boolean;
}

const FileList: React.FC<FileListProps> = ({
  files,
  folders = [],
  currentFile,
  onFileSelect,
  onFileDelete,
  onFileRename,
  onCreateFolder,
  onRenameFolder,
  onMoveFile,
  onMoveFolder,
  onDeleteFolder,
  loading,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className='file-list'>
        <div className='loading'>Loading files...</div>
      </div>
    );
  }

  if (files.length === 0 && folders.length === 0) {
    return (
      <div className='file-list'>
        <div
          style={{
            textAlign: 'center',
            color: '#ccc',
            padding: '20px',
            fontSize: '14px',
          }}
        >
          No files yet. Create your first file or folder to get started!
        </div>
      </div>
    );
  }

  // Build folder tree
  const folderChildren = new Map<string | null, Folder[]>();
  folderChildren.set(null, []);
  folders.forEach(f => {
    const key = (f.parent_id as string | null) || null;
    if (!folderChildren.has(key)) folderChildren.set(key, []);
    folderChildren.get(key)!.push(f);
  });
  const getChildren = (parentId: string | null) =>
    folderChildren.get(parentId) || [];

  const [openMap, setOpenMap] = React.useState<Record<string, boolean>>({});
  const toggleOpen = (id: string) =>
    setOpenMap(prev => ({ ...prev, [id]: !prev[id] }));

  const onDragStartItem = (
    e: React.DragEvent,
    type: 'file' | 'folder',
    id: string
  ) => {
    e.dataTransfer.setData('text/x-type', type);
    e.dataTransfer.setData('text/x-id', id);
  };
  const onDropOnFolder = (
    e: React.DragEvent,
    targetFolderId: string | null
  ) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('text/x-type');
    const id = e.dataTransfer.getData('text/x-id');
    if (!type || !id) return;
    if (type === 'file') onMoveFile && onMoveFile(id, targetFolderId);
    if (type === 'folder' && id !== targetFolderId)
      onMoveFolder && onMoveFolder(id, targetFolderId);
  };

  const renderFiles = (folderId: string | null, depth: number) => {
    return files
      .filter(f => (f.folder_id || null) === folderId)
      .map(file => (
        <div
          key={file.id}
          draggable
          onDragStart={e => onDragStartItem(e, 'file', file.id)}
          onClick={() => onFileSelect(file)}
          className={`ml-${depth * 4} rounded-md border px-3 py-2 hover:bg-muted cursor-pointer ${currentFile?.id === file.id ? 'bg-muted' : ''}`}
        >
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <FileText size={16} />
              <span className='font-medium'>{file.name}</span>
            </div>
            <EditFileDialog
              trigger={
                <Button
                  variant='secondary'
                  size='icon'
                  onClick={e => e.stopPropagation()}
                  title='Edit file'
                >
                  <Settings size={14} />
                </Button>
              }
              currentName={file.name}
              onRename={async newName =>
                onFileRename && onFileRename(file.id, newName)
              }
              onDelete={async () => onFileDelete(file.id)}
            />
          </div>
          <div className='mt-1 text-xs text-muted-foreground flex items-center gap-2'>
            <Calendar size={12} /> {formatDate(file.updated_at)} <span>•</span>{' '}
            {formatFileSize(file.size)}
            {file.user_email && (
              <>
                <span>•</span>
                <span
                  className='text-[10px] opacity-75 truncate max-w-[120px]'
                  title={file.user_email}
                >
                  {file.user_email.length > 20
                    ? `${file.user_email.substring(0, 17)}...`
                    : file.user_email}
                </span>
              </>
            )}
          </div>
        </div>
      ));
  };

  const renderFolder = (folder: Folder, depth: number) => {
    const isOpen = !!openMap[folder.id];
    return (
      <div
        key={folder.id}
        onDragOver={e => e.preventDefault()}
        onDrop={e => onDropOnFolder(e, folder.id)}
        className={`rounded-md border px-2 py-1 ${depth ? `ml-${depth * 4}` : ''}`}
      >
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-1'>
            <button
              className='text-muted-foreground'
              onClick={() => toggleOpen(folder.id)}
            >
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            <div
              draggable
              onDragStart={e => onDragStartItem(e, 'folder', folder.id)}
              className='flex items-center gap-2'
            >
              <FolderIcon size={16} />
              <span className='font-medium'>{folder.name}</span>
            </div>
          </div>
          <div className='flex items-center gap-1'>
            {onCreateFolder && (
              <Button
                variant='secondary'
                size='icon'
                title='New subfolder'
                onClick={() => onCreateFolder(folder.id)}
              >
                <Plus size={14} />
              </Button>
            )}
            {onRenameFolder && (
              <EditFolderDialog
                trigger={
                  <Button
                    variant='secondary'
                    size='icon'
                    title='Folder settings'
                  >
                    <Settings size={14} />
                  </Button>
                }
                currentName={folder.name}
                onRename={newName => onRenameFolder(folder.id, newName)}
                onDelete={
                  onDeleteFolder ? () => onDeleteFolder(folder.id) : undefined
                }
              />
            )}
          </div>
        </div>
        {isOpen && (
          <div className='mt-1 space-y-2'>
            {getChildren(folder.id).map(child =>
              renderFolder(child, depth + 1)
            )}
            {renderFiles(folder.id, depth + 1)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='space-y-2'>
      {/* Root drop zone */}
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={e => onDropOnFolder(e, null)}
      >
        {getChildren(null).map(f => renderFolder(f, 0))}
        {renderFiles(null, 0)}
      </div>
      {onCreateFolder && (
        <Button
          variant='secondary'
          className='w-full'
          onClick={() => onCreateFolder(null)}
        >
          <Plus className='mr-2 h-4 w-4' /> New Folder
        </Button>
      )}
    </div>
  );
};

export default FileList;
